import { Auth } from 'recupero-comunes';

/** Formatea un monto en pesos chilenos; devuelve guion largo si no hay valor. */
export const formatMonto = (value) => {
    if (!value && value !== 0) return '—';
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0
    }).format(value);
}

/** Codifica los parámetros de consulta igual que el resto del módulo (base64 del JSON). */
const encode = (obj) => Buffer.from(JSON.stringify(obj)).toString('base64');

/**
 * Arma el query codificado del tablero Pagos Inter Compañía.
 *
 * 🔴 **Drop 2026-09-22 (RG-541): el período deja de ser un rango de meses.** Ya no viaja
 *    `mesDesde`/`mesHasta`: el filtro es un rango de **fechas exactas** (`fechaDesde` →
 *    `fechaHasta`, YYYY-MM-DD) y el backend filtra directo por `vep_dat_fecPago`.
 */
export const buildQuery = (filtros = {}, extra = {}) => {
    const { rutUsuario, rutOrganizacion } = Auth.currentUser() || {};
    return encode({
        rutcia: rutOrganizacion || '',
        rutusr: rutUsuario || '',
        tipoInforme: 'Defensa entre CIA',
        compania: filtros.compania || null,
        fechaDesde: filtros.fechaDesde || null,
        fechaHasta: filtros.fechaHasta || null,
        ...extra
    });
}

/**
 * Rango contable inicial 10 → 10: si hoy es día ≥10, va del día 10 del mes actual al día 10 del
 * siguiente; si es día <10, va del día 10 del mes anterior al día 10 del actual. Es el rango con
 * el que abre el tablero Inter Compañía y al que vuelve el botón Limpiar.
 */
export const rangoContableInicial = (hoy = new Date()) => {
    const dia = hoy.getDate();
    const mesRef = dia >= 10 ? hoy.getMonth() : hoy.getMonth() - 1;
    const desde = new Date(hoy.getFullYear(), mesRef, 10);
    const hasta = new Date(hoy.getFullYear(), mesRef + 1, 10);
    return { fechaDesde: toISO(desde), fechaHasta: toISO(hasta) };
}

/** Suma un mes calendario preservando el día — usada al mover "Fecha Desde" del filtro Inter Compañía. */
export const sumarUnMes = (isoDate) => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-').map(Number);
    return toISO(new Date(y, m, d));
}

const toISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Formatea 'YYYY-MM-DD' a 'DD/MM/YYYY'. */
export const formatFecha = (isoDate) => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${d}/${m}/${y}`;
}

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** Recibe 'YYYY-MM' y devuelve 'Mes YYYY' (ej: '2026-08' → 'Agosto 2026'). */
export const formatMesAnio = (yyyyMm) => {
    if (!yyyyMm) return '';
    const [y, m] = yyyyMm.split('-').map(Number);
    return `${MESES_ES[m - 1]} ${y}`;
}

/**
 * Agrega campos a un query ya codificado (base64 del JSON) sin perder los que traía.
 * Lo usa la exportación de casos (RG-602): parte de la query con que se cargó el tablero y le suma
 * el `concepto` y su recorte.
 */
export const ampliaQuery = (queryBase64, extra = {}) => {
    const base = queryBase64 ? JSON.parse(Buffer.from(queryBase64, 'base64').toString('utf8')) : {};
    return encode({ ...base, ...extra });
}

/** Query codificada para consultar el estado de un trabajo de exportación. */
export const buildQueryEstado = (jobId) => encode({ jobId });

/**
 * Dispara la descarga desde una URL firmada de S3. La respuesta trae `Content-Disposition: attachment`
 * con el nombre del archivo, así que el navegador baja el archivo sin salir de la página y sin CORS.
 */
export const descargaUrl = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/** Arma el query codificado del tablero Recupero Directo. */
export const buildQueryDirecto = (filtros = {}, extra = {}) => {
    const { rutUsuario, rutOrganizacion } = Auth.currentUser() || {};
    return encode({
        rutcia: rutOrganizacion || '',
        rutusr: rutUsuario || '',
        tipoInforme: 'Recupero Directo',
        responsableId: filtros.responsableId || null,
        mes: filtros.mes || null,
        fechadesde: filtros.fechadesde || '',
        fechahasta: filtros.fechahasta || '',
        criteriosFecha: filtros.criteriosFecha || null,
        ...extra
    });
}
